import { Attachment01Icon } from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import { useSuspenseQuery } from "@tanstack/react-query"
import { CURRENCIES, CURRENCY_SYMBOLS } from "@unfiddle/core/currency/constants"
import type { Currency } from "@unfiddle/core/currency/types"
import {
   ORDER_PAYMENT_TYPES,
   ORDER_PAYMENT_TYPES_TRANSLATION,
   ORDER_SEVERITIES,
   ORDER_SEVERITIES_TRANSLATION,
} from "@unfiddle/core/order/constants"
import type { OrderItem } from "@unfiddle/core/order/item/types"
import type {
   OrderPaymentType,
   OrderSeverity,
} from "@unfiddle/core/order/types"
import type { RouterInput, RouterOutput } from "@unfiddle/core/trpc/types"
import { Button } from "@unfiddle/ui/components/button"
import { DateInput } from "@unfiddle/ui/components/date-input"
import {
   Field,
   FieldControl,
   FieldGroup,
   FieldLabel,
   Fieldset,
   FieldsetLegend,
} from "@unfiddle/ui/components/field"
import { Icons } from "@unfiddle/ui/components/icons"
import { NumberField } from "@unfiddle/ui/components/number-field"
import {
   Select,
   SelectItem,
   SelectPopup,
   SelectTrigger,
   SelectTriggerIcon,
   SelectValue,
} from "@unfiddle/ui/components/select"
import { Textarea } from "@unfiddle/ui/components/textarea"
import { formData } from "@unfiddle/ui/utils"
import * as React from "react"
import { UploadedAttachment } from "@/attachment/components/uploaded-attachment"
import { useAttachments } from "@/attachment/hooks"
import { useAuth } from "@/auth/hooks"
import { ClientCombobox } from "@/client/components/client-combobox"
import { FileUploader } from "@/file/components/uploader"
import { trpc } from "@/trpc"
import { SuspenseBoundary } from "@/ui/components/suspense-boundary"

type BareItem = Omit<OrderItem, "id">

type FormData = {
   name: string
   sellingPrice: string
   note: string
   crmUrl: string
   clientId: string | null
   severity: OrderSeverity
   paymentType: OrderPaymentType
   deliversAt: Date | null
   currency: Currency
   items: BareItem[]
   attachments: RouterInput["order"]["create"]["attachments"]
}

interface Props {
   orderId?: string | undefined
   onSubmit: (data: FormData) => void
   children: React.ReactNode
   open?: boolean | undefined
}

export function OrderForm(props: Props) {
   if (props.orderId && props.open) return <WithExistingData {...props} />

   return <Form {...props} />
}

function WithExistingData(props: Props) {
   const auth = useAuth()
   const order = useSuspenseQuery(
      trpc.order.one.queryOptions({
         orderId: props.orderId ?? "",
         workspaceId: auth.workspace.id,
      }),
   ).data

   if (order === null)
      return (
         <p className="absolute inset-0 m-auto size-fit text-center text-muted">
            Замовлення не знайдено. Можливо, його видалили.
         </p>
      )

   return (
      <Form
         order={order}
         {...props}
      />
   )
}

function Form({
   order,
   onSubmit,
   children,
}: { order?: RouterOutput["order"]["one"] } & Props) {
   const auth = useAuth()
   const [deliversAt, setDeliversAt] = React.useState(order?.deliversAt ?? null)
   const [currency, setCurrency] = React.useState(order?.currency ?? "UAH")
   const [items, setItems] = React.useState<BareItem[]>([
      {
         name: "",
         quantity: 1,
         desiredPrice: null,
         assignees: [],
      },
   ])
   const [clientId, setClientId] = React.useState(order?.clientId ?? undefined)
   const formRef = React.useRef<HTMLFormElement>(null)
   const fileUploaderRef = React.useRef<HTMLDivElement>(null)
   const attachments = useAttachments({ subjectId: auth.workspace.id })

   return (
      <form
         ref={formRef}
         className="mt-4 flex grow flex-col space-y-4"
         onSubmit={(e) => {
            e.preventDefault()
            const activeElement = document.activeElement as HTMLElement
            if (activeElement && formRef.current?.contains(activeElement)) {
               activeElement.blur()
            }

            requestAnimationFrame(() => {
               const form = formData<FormData>(e.target)
               onSubmit({
                  ...form,
                  deliversAt,
                  currency,
                  items,
                  clientId: clientId ?? null,
                  attachments: attachments.uploaded,
               })
            })
         }}
      >
         <FieldGroup className="grid-cols-[1fr_74px] md:grid-cols-[1fr_74px]">
            <Field>
               <FieldLabel>Назва</FieldLabel>
               <FieldControl
                  required
                  placeholder="Уведіть назву"
                  name="name"
                  defaultValue={order?.name ?? ""}
               />
            </Field>
            <Field>
               <FieldLabel className={"mb-2"}>Валюта</FieldLabel>
               <Select
                  value={currency}
                  onValueChange={(v) => {
                     if (!v) return
                     setCurrency(v)
                  }}
               >
                  <SelectTrigger />
                  <SelectPopup align="start">
                     {CURRENCIES.map((item) => (
                        <SelectItem
                           key={item}
                           value={item}
                        >
                           {item}
                        </SelectItem>
                     ))}
                  </SelectPopup>
               </Select>
            </Field>
         </FieldGroup>
         {order ? null : (
            <Fieldset className={"space-y-2"}>
               <FieldsetLegend className={"md:mb-4"}>Товари</FieldsetLegend>
               <FieldGroup className="max-md:hidden md:grid-cols-[1fr_4rem_5rem_2rem] md:gap-5">
                  <p className="font-medium text-sm">Назва</p>
                  <p className="font-medium text-sm">Кількість</p>
                  <p className="font-medium text-sm">Баж. ціна</p>
               </FieldGroup>
               {items.map((item, idx) => (
                  <FieldGroup
                     key={idx}
                     className="grid-cols-[1fr_1fr_2rem] md:grid-cols-[1fr_4rem_5rem_2rem] md:gap-5"
                  >
                     <Field className={"max-md:col-span-3"}>
                        <FieldControl
                           data-testid="order-item-name"
                           required
                           placeholder="Уведіть назву"
                           value={item.name}
                           onChange={(e) =>
                              setItems((prevItems) =>
                                 prevItems.map((i, itemIdx) =>
                                    idx === itemIdx
                                       ? { ...i, name: e.target.value }
                                       : i,
                                 ),
                              )
                           }
                           // Add a class name to easily select this input
                           className="order-item-name-input"
                        />
                     </Field>
                     <Field>
                        <NumberField
                           data-testid="order-item-quantity"
                           required
                           placeholder="шт."
                           min={1}
                           value={item.quantity}
                           onValueChange={(quantity) =>
                              setItems(
                                 items.map((i, itemIdx) =>
                                    idx === itemIdx
                                       ? { ...i, quantity: quantity ?? 1 }
                                       : i,
                                 ),
                              )
                           }
                        />
                     </Field>
                     <Field>
                        <NumberField
                           data-testid="order-item-price"
                           step="any"
                           placeholder={CURRENCY_SYMBOLS[currency]}
                           value={item.desiredPrice}
                           onValueChange={(desiredPrice) =>
                              setItems(
                                 items.map((i, itemIdx) =>
                                    idx === itemIdx
                                       ? { ...i, desiredPrice }
                                       : i,
                                 ),
                              )
                           }
                        />
                     </Field>
                     <Button
                        onClick={() =>
                           setItems(
                              items.filter((_, itemIdx) => idx !== itemIdx),
                           )
                        }
                        type="button"
                        variant={"ghost"}
                        kind={"icon"}
                        disabled={items.length === 1}
                        className="self-end disabled:cursor-not-allowed"
                        data-testid="trash-button"
                     >
                        <HugeiconsIcon icon={Icons.trash} />
                     </Button>
                  </FieldGroup>
               ))}
               <Button
                  onClick={() => {
                     setItems((prevItems) => [
                        ...prevItems,
                        {
                           name: "",
                           quantity: 1,
                           desiredPrice: null,
                           assignees: [],
                        },
                     ])

                     // Use requestAnimationFrame to wait for the DOM to update
                     requestAnimationFrame(() => {
                        // Find all the item name inputs
                        const itemInputs = document.querySelectorAll(
                           ".order-item-name-input",
                        ) as NodeListOf<HTMLInputElement>
                        // Focus the last one (which is the newly added one)
                        if (itemInputs.length > 0) {
                           itemInputs[itemInputs.length - 1]?.focus()
                        }
                     })
                  }}
                  type="button"
                  className="mt-2 w-full disabled:cursor-not-allowed"
                  variant={"secondary"}
               >
                  <HugeiconsIcon icon={Icons.plus} />
                  Додати товар
               </Button>
            </Fieldset>
         )}
         <Fieldset className={"mb-10 space-y-4 md:space-y-8"}>
            <FieldsetLegend className={"md:mb-4"}>Деталі</FieldsetLegend>
            <FieldGroup>
               <Field>
                  <FieldLabel>Ціна продажу</FieldLabel>
                  <NumberField
                     step="any"
                     defaultValue={
                        order?.sellingPrice === 0
                           ? undefined
                           : order?.sellingPrice
                     }
                     name="sellingPrice"
                     placeholder={CURRENCY_SYMBOLS[currency]}
                  />
               </Field>
               <SuspenseBoundary
                  fallback={null}
                  errorComponent={null}
               >
                  <ClientCombobox
                     clientId={clientId}
                     setClientId={setClientId}
                  />
               </SuspenseBoundary>
            </FieldGroup>
            <Field>
               <label
                  htmlFor="term"
                  className="font-medium text-sm"
               >
                  Термін постачання
               </label>
               <DateInput
                  required
                  inDialog
                  id="term"
                  value={deliversAt}
                  onValueChange={setDeliversAt}
               />
            </Field>
            <FieldGroup>
               <Field>
                  <FieldLabel className={"mb-2.5"}>Пріоритет</FieldLabel>
                  <Select
                     name="severity"
                     defaultValue={order?.severity ?? "low"}
                  >
                     <SelectTrigger
                        render={
                           <Button
                              variant={"secondary"}
                              className="w-full justify-start"
                           >
                              <SelectValue>
                                 {(v) =>
                                    ORDER_SEVERITIES_TRANSLATION[v as never]
                                 }
                              </SelectValue>
                              <SelectTriggerIcon />
                           </Button>
                        }
                     />
                     <SelectPopup align="start">
                        {ORDER_SEVERITIES.map((s) => (
                           <SelectItem
                              key={s}
                              value={s}
                              label={ORDER_SEVERITIES_TRANSLATION[s]}
                           >
                              {ORDER_SEVERITIES_TRANSLATION[s]}
                           </SelectItem>
                        ))}
                     </SelectPopup>
                  </Select>
               </Field>
               <Field>
                  <FieldLabel className={"mb-2.5"}>Варіант оплати</FieldLabel>
                  <Select
                     name="paymentType"
                     defaultValue={order?.paymentType ?? "cash"}
                  >
                     <SelectTrigger
                        render={
                           <Button
                              variant={"secondary"}
                              className="w-full justify-start"
                           >
                              <SelectValue>
                                 {(v) =>
                                    ORDER_PAYMENT_TYPES_TRANSLATION[v as never]
                                 }
                              </SelectValue>
                              <SelectTriggerIcon />
                           </Button>
                        }
                     />
                     <SelectPopup align="start">
                        {ORDER_PAYMENT_TYPES.map((s) => (
                           <SelectItem
                              key={s}
                              value={s}
                              label={ORDER_PAYMENT_TYPES_TRANSLATION[s]}
                           >
                              {ORDER_PAYMENT_TYPES_TRANSLATION[s]}
                           </SelectItem>
                        ))}
                     </SelectPopup>
                  </Select>
               </Field>
            </FieldGroup>
            <div>
               <Field>
                  <FieldLabel>Комент</FieldLabel>
                  <div className="relative w-full">
                     <FieldControl
                        render={
                           <Textarea
                              className="pr-12"
                              name="note"
                              placeholder="Додайте комент"
                              defaultValue={order?.note}
                              onPaste={(e) => attachments.onPaste(e)}
                           />
                        }
                     />
                     {order ? null : (
                        <Button
                           type="button"
                           onClick={() => {
                              fileUploaderRef.current?.click()
                           }}
                           kind={"icon"}
                           variant={"ghost"}
                           className="absolute top-1 right-1"
                        >
                           <HugeiconsIcon icon={Attachment01Icon} />
                        </Button>
                     )}
                  </div>
               </Field>
               <div className="mt-4 flex flex-wrap gap-2 empty:hidden">
                  {attachments.uploaded.map((file, idx) => (
                     <UploadedAttachment
                        key={idx}
                        file={file}
                        onRemove={() => attachments.remove(file.id)}
                     />
                  ))}
               </div>
            </div>
            <Field>
               <FieldLabel>Посилання на замовлення в CRM</FieldLabel>
               <FieldControl
                  type="url"
                  name="crmUrl"
                  placeholder="https://luckyskyforce.keycrm.app/app/orders/..."
                  defaultValue={order?.crmUrl ?? ""}
               />
            </Field>
            {/* <Field className={"mt-3 flex flex-row items-center gap-2"}>
               <Checkbox
                  name="vat"
                  defaultChecked={order?.vat ?? false}
               />
               <FieldLabel className={"mt-px"}>З ПДВ</FieldLabel>
            </Field> */}
         </Fieldset>
         <FileUploader
            ref={fileUploaderRef}
            className="absolute inset-0 z-9 h-full"
            onUpload={attachments.upload.mutateAsync}
         />
         {children}
      </form>
   )
}
